import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import type { NapkinWisdom } from "@shared/schema"
import { 
  Lightbulb,
  TrendingUp,
  Filter,
  Eye,
  CheckCircle,
  Clock,
  Play,
  Trash2
} from "lucide-react"
import { FloatingCard } from "@/components/FloatingCard"
import { AnimatedSection } from "@/components/AnimatedSection"
import { NewsPostsModal } from "@/components/NewsPostsModal"
import { RelevanceScore } from "@/components/RelevanceScore"
import { statusConfig } from "@/lib/status-config"

export default function NapkinWisdomPage() {
  const [selectedStatus, setSelectedStatus] = useState<string>("all")
  const [selectedWisdom, setSelectedWisdom] = useState<NapkinWisdom | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null)
  const [activeVideoTitle, setActiveVideoTitle] = useState<string>("")
  const [deleteConfirmWisdom, setDeleteConfirmWisdom] = useState<NapkinWisdom | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: async (wisdomId: string) => {
      await apiRequest('DELETE', `/api/napkin-wisdom/${wisdomId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/napkin-wisdom'] })
      toast({
        title: "Gelöscht",
        description: "Die Napkin Wisdom wurde gelöscht."
      })
      setDeleteConfirmWisdom(null)
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Löschen fehlgeschlagen.",
        variant: "destructive"
      })
    }
  })

  const handleViewPosts = (wisdom: NapkinWisdom) => {
    setSelectedWisdom(wisdom)
    setIsModalOpen(true)
  }

  // Transform Dropbox URLs for direct browser playback
  const getPlayableVideoUrl = (url: string): string => {
    if (url.includes('dropbox.com')) {
      // Replace dl=0 or dl=1 with raw=1 for direct playback
      let playableUrl = url
        .replace(/[?&]dl=0/, '?raw=1')
        .replace(/[?&]dl=1/, '?raw=1');
      // If no dl parameter, add raw=1
      if (!playableUrl.includes('raw=1')) {
        playableUrl = playableUrl + (playableUrl.includes('?') ? '&raw=1' : '?raw=1');
      }
      return playableUrl;
    }
    return url;
  }

  const handlePlayVideo = (wisdom: NapkinWisdom) => {
    if (wisdom.videoUrl) {
      setActiveVideoUrl(getPlayableVideoUrl(wisdom.videoUrl))
      setActiveVideoTitle(wisdom.title)
      setVideoModalOpen(true)
    }
  }

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open)
    if (!open) {
      setSelectedWisdom(null)
    }
  }

  // Poll if any have pending status
  const { data: wisdomList = [], isLoading, error } = useQuery<NapkinWisdom[]>({
    queryKey: ['/api/napkin-wisdom'],
    refetchInterval: (query) => {
      const data = query.state.data as NapkinWisdom[] | undefined
      const hasPending = data?.some(wisdom => wisdom.status === 'pending')
      return hasPending ? 5000 : false
    }
  })

  const filteredWisdom = wisdomList.filter(wisdom => {
    if (selectedStatus === "all") return true
    return wisdom.status === selectedStatus
  })

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground">Lade Napkin Wisdom...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Fehler beim Laden der Napkin Wisdom.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <AnimatedSection>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="heading-napkin-wisdom">
              <Lightbulb className="h-8 w-8 text-amber-500" />
              Napkin Wisdom
            </h1>
            <p className="text-muted-foreground mt-2">
              Zeitlose Business-Weisheiten mit handgezeichneten Napkin-Sketches
            </p>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt Wisdom</CardTitle>
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-total-wisdom">{wisdomList.length}</div>
              <p className="text-xs text-muted-foreground">Business-Insights</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ausstehend</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-pending">
                {wisdomList.filter(w => w.status === 'pending').length}
              </div>
              <p className="text-xs text-muted-foreground">Zur Überprüfung</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Freigegeben</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-approved">
                {wisdomList.filter(w => w.status === 'approved').length}
              </div>
              <p className="text-xs text-muted-foreground">Bereit zum Posten</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Hohe Relevanz</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-high-relevance">
                {wisdomList.filter(w => (w.relevanceScore ?? 0) >= 70).length}
              </div>
              <p className="text-xs text-muted-foreground">Score ≥ 70/100</p>
            </CardContent>
          </FloatingCard>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={200}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Status</label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder="Status wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="pending">Ausstehend</SelectItem>
                    <SelectItem value="approved">Freigegeben</SelectItem>
                    <SelectItem value="rejected">Abgelehnt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedStatus("all")}
                  data-testid="button-clear-filters"
                >
                  Filter zurücksetzen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </AnimatedSection>

      <AnimatedSection delay={300}>
        {filteredWisdom.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">Keine Napkin Wisdom gefunden.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredWisdom.map((wisdom) => {
              const statusInfo = statusConfig[wisdom.status as keyof typeof statusConfig]
              const StatusIcon = statusInfo?.icon || Clock

              return (
                <FloatingCard key={wisdom.id} className="hover-elevate">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      {(wisdom.videoUrl || wisdom.thumbnailUrl || wisdom.imageUrl) && (
                        <div 
                          className={`relative w-full sm:w-32 sm:h-32 sm:order-2 aspect-video sm:aspect-square rounded-md overflow-hidden bg-muted ${wisdom.videoUrl ? 'cursor-pointer group' : ''}`}
                          onClick={wisdom.videoUrl ? () => handlePlayVideo(wisdom) : undefined}
                          data-testid={wisdom.videoUrl ? `video-trigger-${wisdom.id}` : undefined}
                        >
                          {wisdom.videoUrl ? (
                            <>
                              {wisdom.thumbnailUrl ? (
                                <img 
                                  src={wisdom.thumbnailUrl}
                                  alt={wisdom.title}
                                  className="w-full h-full object-cover"
                                  data-testid={`wisdom-thumbnail-${wisdom.id}`}
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-600" />
                              )}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                                <div className="bg-white/90 rounded-full p-3 group-hover:scale-110 transition-transform">
                                  <Play className="w-5 h-5 text-gray-800 fill-gray-800" />
                                </div>
                              </div>
                            </>
                          ) : wisdom.imageUrl ? (
                            <img 
                              src={wisdom.imageUrl} 
                              alt={wisdom.title}
                              className="w-full h-full object-cover"
                              data-testid={`wisdom-image-${wisdom.id}`}
                            />
                          ) : null}
                        </div>
                      )}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          {statusInfo && (
                            <Badge className={statusInfo.color} data-testid={`badge-status-${wisdom.id}`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusInfo.label}
                            </Badge>
                          )}
                          <Badge variant="outline" data-testid={`badge-source-${wisdom.id}`}>
                            {wisdom.sourceName || 'Napkin Wisdom'}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl" data-testid={`wisdom-title-${wisdom.id}`}>
                          {wisdom.title}
                        </CardTitle>
                        {wisdom.summary && (
                          <CardDescription className="line-clamp-2" data-testid={`wisdom-summary-${wisdom.id}`}>
                            {wisdom.summary}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Relevanz-Score</p>
                        <RelevanceScore score={wisdom.relevanceScore} />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {wisdom.videoUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePlayVideo(wisdom)}
                            className="w-full sm:w-auto"
                            data-testid={`button-play-video-${wisdom.id}`}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Video abspielen
                          </Button>
                        )}
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleViewPosts(wisdom)}
                          className="w-full sm:w-auto"
                          data-testid={`button-view-posts-${wisdom.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Posts ansehen (3)
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirmWisdom(wisdom)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`button-delete-${wisdom.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </FloatingCard>
              )
            })}
          </div>
        )}
      </AnimatedSection>

      {selectedWisdom && (
        <NewsPostsModal
          open={isModalOpen}
          onOpenChange={handleModalClose}
          newsItem={selectedWisdom as any}
          sourceType="napkin_wisdom"
        />
      )}

      <Dialog 
        open={videoModalOpen} 
        onOpenChange={(open) => {
          setVideoModalOpen(open)
          if (!open) {
            setActiveVideoUrl(null)
            setActiveVideoTitle("")
          }
        }}
      >
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" />
              {activeVideoTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="relative aspect-video bg-black">
            {activeVideoUrl && (
              <video
                src={activeVideoUrl}
                controls
                autoPlay
                className="w-full h-full"
                data-testid="video-player"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmWisdom} onOpenChange={(open) => !open && setDeleteConfirmWisdom(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Napkin Wisdom löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Napkin Wisdom wirklich löschen? 
              Alle zugehörigen Posts werden ebenfalls gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmWisdom && deleteMutation.mutate(deleteConfirmWisdom.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Löschen..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
